import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Tooltip } from "antd";
import { Avatar } from "../../../components/visuals/Avatar";
import { TaskKindIcon } from "../../../components/visuals/TaskKindIcon";
import { LegendCircle } from "../../../components/visuals/styledComponents";
import {
  IconStack,
  IconStackItem,
} from "../../../components/visuals/IconStack";
import {
  Task,
  TaskSubjectPreview,
  WsQParamType,
} from "../../../models/workflowModels";
import { ROUTES } from "../../../routes/routeNames";
import {
  compassBordeaux,
  compassLightGreyBackground,
  greyText,
  lightGreyOutline,
  lightGreyText,
} from "../../../styles";
import { formatReadable } from "../../../utils/formattingUtils";
import { MESSAGES } from "../../../messages";
import { ENGAGEMENT_META_THEME_IMAGES } from "../../CompanyEngagements/constants";
import {
  SUBJECT_TYPE_LABELS,
  TaskDateTone,
  isSubjectType,
  taskDateLabel,
  taskStatusDotColor,
  taskStatusWord,
} from "./actionTrackerUtils";

type ActionTrackerRowProps = {
  task: Task;
};

const RowLink = styled(Link)`
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) 20px 96px;
  align-items: center;
  column-gap: 10px;
  padding: 9px 6px;
  border-bottom: 1px solid ${lightGreyOutline};
  text-decoration: none;
  transition: background-color 0.15s ease;
  &:hover,
  &:focus-visible {
    background-color: ${compassLightGreyBackground};
  }
  @container action-tracker (max-width: 420px) {
    grid-template-columns: 14px minmax(0, 1fr) 84px;
  }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const Title = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  word-break: break-word;
  @container action-tracker (max-width: 420px) {
    -webkit-line-clamp: 2;
  }
  font-family: "Apercu";
  font-size: 13px;
  line-height: 18px;
  color: ${greyText};
`;

const SubjectLine = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-family: "Apercu";
  font-size: 11px;
  line-height: 16px;
  color: ${lightGreyText};
`;

const SubjectText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubjectPart = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  white-space: nowrap;
  &::before {
    content: "·";
  }
`;

const TypePart = styled(SubjectPart)`
  flex-shrink: 0;
`;

const MetaThemeMark = styled.img`
  width: 12px;
  height: 12px;
  object-fit: contain;
  display: block;
`;

const OwnerCell = styled.span`
  display: flex;
  justify-content: center;
  @container action-tracker (max-width: 420px) {
    display: none;
  }
`;

const StatusCell = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const StatusWord = styled.span`
  display: flex;
  align-items: center;
  font-family: "Apercu";
  font-size: 12px;
  line-height: 18px;
  color: ${greyText};
  white-space: nowrap;
`;

const DATE_TONE_COLORS: Record<TaskDateTone, string> = {
  [TaskDateTone.DUE]: greyText,
  [TaskDateTone.OVERDUE]: compassBordeaux,
  [TaskDateTone.CREATED]: lightGreyText,
};

const DateText = styled.span<{ $tone: TaskDateTone }>`
  padding-left: 12px;
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 11px;
  line-height: 16px;
  color: ${({ $tone }): string => DATE_TONE_COLORS[$tone]};
  white-space: nowrap;
`;

const taskLink = (taskId: string): { pathname: string; search: string } => ({
  pathname: `${ROUTES.WORKSPACE}/${WsQParamType.VIEW_TASK}`,
  search: `?${new URLSearchParams({ taskId }).toString()}`,
});

const toSubjectStackItem = (subject: TaskSubjectPreview): IconStackItem => ({
  key: subject.subject_id,
  label: formatReadable(subject.label),
  image: subject.image,
});

const TaskSubject = ({ task }: ActionTrackerRowProps): JSX.Element => {
  const subject = task.subjects?.[0];
  const typeLabel = isSubjectType(task.subject_type)
    ? SUBJECT_TYPE_LABELS[task.subject_type]
    : null;

  if (!subject) {
    return (
      <SubjectLine>
        <SubjectText>
          {typeLabel ?? MESSAGES.ACTION_TRACKER.GENERAL_TASK}
        </SubjectText>
      </SubjectLine>
    );
  }

  const subjects = task.subjects ?? [];
  const extra = subjects.length - 1;
  const subjectTypeLabel = SUBJECT_TYPE_LABELS[subject.subject_type];

  return (
    <SubjectLine>
      <IconStack items={subjects.map(toSubjectStackItem)} size={12}>
        <SubjectText>
          {formatReadable(subject.label)}
          {extra > 0 && ` +${extra}`}
        </SubjectText>
      </IconStack>
      {subjectTypeLabel && (
        <TypePart>
          {subject.meta_theme && (
            <Tooltip title={subject.meta_theme}>
              <MetaThemeMark
                src={ENGAGEMENT_META_THEME_IMAGES[subject.meta_theme].logo}
                alt={subject.meta_theme}
              />
            </Tooltip>
          )}
          {subjectTypeLabel}
        </TypePart>
      )}
      {subject.context && (
        <SubjectPart>
          <SubjectText>{subject.context}</SubjectText>
        </SubjectPart>
      )}
    </SubjectLine>
  );
};

export const ActionTrackerRow = ({
  task,
}: ActionTrackerRowProps): JSX.Element => {
  const date = taskDateLabel(task);
  return (
    <RowLink to={taskLink(task.task_id)}>
      <TaskKindIcon isFlag={task.is_flag} status={task.status} />
      <Main>
        <Tooltip title={task.title} placement="topLeft" mouseEnterDelay={0.4}>
          <Title>{task.title}</Title>
        </Tooltip>
        <TaskSubject task={task} />
      </Main>
      <OwnerCell>
        <Avatar username={task.assigned_to} size="xsmall" tooltip={true} />
      </OwnerCell>
      <StatusCell>
        <StatusWord>
          <LegendCircle color={taskStatusDotColor(task)} size={7} />
          {taskStatusWord(task)}
        </StatusWord>
        <DateText $tone={date.tone}>{date.text}</DateText>
      </StatusCell>
    </RowLink>
  );
};
